import React, { useState, useEffect } from 'react';
import { Menu, X, LogOut, User, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
const logoImg = "/logo.png";

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, profile, loading, signOut } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const getDashboardPath = () => {
    if (!profile?.role) return '/builder-dashboard';
    switch (profile.role) {
      case 'seller': return '/seller-dashboard';
      case 'buyer': return '/buyer-dashboard';
      case 'professional': return '/professional-dashboard';
      default: return '/builder-dashboard';
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 text-ink ${scrolled ? 'bg-canvas/90 backdrop-blur-xl border-b border-ink/8 shadow-soft' : 'bg-canvas/40 backdrop-blur-sm border-b border-transparent'}`}>
      <div className="max-w-[960px] mx-auto px-8 md:px-12 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img
            src={logoImg}
            alt="REALV3"
            width={400}
            height={170}
            fetchPriority="high"
            decoding="async"
            className="h-14 md:h-16 w-auto"
          />
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center space-x-10 text-sm font-medium tracking-wide">
          <Link to="/about" className={`transition-all duration-400 hover:text-gold-deep ${isActive('/about') ? 'text-gold-deep' : ''}`}>
            How it works
          </Link>
          <Link to="/listings" className={`transition-all duration-400 hover:text-gold-deep ${isActive('/listings') ? 'text-gold-deep' : ''}`}>
            Listings
          </Link>
          <Link to="/professionals" className={`transition-all duration-400 hover:text-gold-deep ${isActive('/professionals') ? 'text-gold-deep' : ''}`}>
            Professionals
          </Link>

          {!loading && (
            user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center space-x-1.5 hover:text-gold-deep transition-colors duration-400">
                  <User className="h-4 w-4" strokeWidth={1.5} />
                  <span>{profile?.full_name || 'Account'}</span>
                  <ChevronDown className="h-3 w-3" strokeWidth={1.5} />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-card border-border shadow-lift">
                  <DropdownMenuItem asChild>
                    <Link to={getDashboardPath()} className="cursor-pointer">
                      My Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/list-property" className="cursor-pointer">
                      List a Home
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={signOut} className="cursor-pointer">
                    <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                asChild
                size="sm"
                className="bg-gold hover:bg-gold-deep text-ink font-medium tracking-wide px-6 py-2.5 rounded-md transition-all duration-500 shadow-soft hover:shadow-lift"
              >
                <Link to="/auth">Get started</Link>
              </Button>
            )
          )}
        </div>

        {/* Mobile toggle */}
        <button className="md:hidden" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label={isMenuOpen ? "Close menu" : "Open menu"} aria-expanded={isMenuOpen}>
          {isMenuOpen ? <X className="h-6 w-6" strokeWidth={1.5} /> : <Menu className="h-6 w-6" strokeWidth={1.5} />}
        </button>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden px-8 pb-6 space-y-4 text-sm font-medium border-t border-ink/8 pt-5 bg-canvas/95 backdrop-blur-xl">
          <Link to="/about" onClick={() => setIsMenuOpen(false)} className="block py-1">How it works</Link>
          <Link to="/listings" onClick={() => setIsMenuOpen(false)} className="block py-1">Listings</Link>
          <Link to="/professionals" onClick={() => setIsMenuOpen(false)} className="block py-1">Professionals</Link>
          {!loading && (
            user ? (
              <>
                <Link to={getDashboardPath()} onClick={() => setIsMenuOpen(false)} className="block py-1">
                  My Dashboard
                </Link>
                <Link to="/list-property" onClick={() => setIsMenuOpen(false)} className="block py-1">
                  List a Home
                </Link>
                <button onClick={() => { signOut(); setIsMenuOpen(false); }} className="block py-1">Sign Out</button>
              </>
            ) : (
              <Link to="/auth" onClick={() => setIsMenuOpen(false)} className="block py-3 mt-2 text-center bg-gold text-ink font-medium rounded-md tracking-wide">
                Get started
              </Link>
            )
          )}
        </div>
      )}
    </nav>
  );
};

export default Navigation;
